/**
 * ADR-218 — plain cart links: any page URL with `?add=<variantId:qty,…>`.
 *
 * Desk builds `/cart?add=…` (layout/theme.liquid sends it to the home page before render).
 * The lines go in through /cart/add.js at full price (gift cards removed, per-line cap
 * kept), the param is dropped in place, the drawer refreshes and opens. No note: the
 * drawer shows the products. ADR-289: `eros_assistant=<id>` on the link is kept for our
 * checkout (ErosAssistantAttribution.current). The cart transport here (ErosCartAdd) is shared with the
 * `?reorder=` landing and the offer landing; the wire rules are eros-reorder-wire.js.
 */
(function (root) {
  'use strict';
  var isNode = typeof module !== 'undefined' && !!module.exports;
  if (root.ErosCartAdd) return; /* the cart page and the drawer both load this file */
  var W = isNode ? require('./eros-reorder-wire.js') : root.ErosReorderWire;
  if (!W) return;

  function cartRoot() {
    var r = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return r.replace(/\/?$/, '/');
  }

  function post(path, body) {
    return fetch(cartRoot() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (payload) {
        return { ok: res.ok, payload: payload };
      });
    });
  }

  /* Add one line; a quantity above the stock left (422) is retried as 1. */
  function addLine(line) {
    return post('cart/add.js', { id: Number(line.variantId), quantity: line.quantity }).then(function (r) {
      if (r.ok) return r.payload;
      if (line.quantity > 1) {
        return post('cart/add.js', { id: Number(line.variantId), quantity: 1 }).then(function (r2) {
          return r2.ok ? r2.payload : null;
        });
      }
      return null;
    });
  }

  /**
   * Add [{variantId, quantity}] one after the other; resolves with how many went in.
   * One failed line never stops the rest. Gift cards follow their own checkout rules
   * (ADR-200): they are taken straight out again and do not count.
   */
  function addLines(lines) {
    var added = 0;
    return lines
      .reduce(function (chain, line) {
        return chain.then(function () {
          return addLine(line)
            .then(function (item) {
              if (!item) return undefined;
              if (item.gift_card) return post('cart/change.js', { id: item.key, quantity: 0 });
              added += 1;
              return undefined;
            })
            .catch(function () {});
        });
      }, Promise.resolve())
      .then(function () {
        return added;
      });
  }

  /** Merged with lines already in the cart: bring every line back under the per-line cap. */
  function capQuantities() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        return W.overCap(cart.items).reduce(function (p, c) {
          return p.then(function () {
            return post('cart/change.js', { id: c.key, quantity: c.quantity });
          });
        }, Promise.resolve());
      })
      .catch(function () {});
  }

  function openDrawer() {
    if (root.ErosOfferRender) return root.ErosOfferRender.openDrawer();
    if (!root.customElements) return undefined;
    return root.customElements.whenDefined('cart-drawer').then(function () {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show();
    });
  }

  /* ADR-289: a Venus link carries `eros_assistant=<conversation id>`. Only the id is kept
     ({id, at}, 7 days, the last link wins, the eros_ref pattern); our checkout sends it as
     `assistant` and the order gets `_eros_assistant`. Never a Shopify cart attribute. */
  var ASSISTANT_PARAM = 'eros_assistant';
  var ASSISTANT_KEY = 'eros_assistant_v1';
  var ASSISTANT_TTL_MS = 7 * 24 * 3600 * 1000;
  var ASSISTANT_ID = /^[A-Za-z][A-Za-z0-9_:-]{3,79}$/; /* starts with a letter: a phone number can never pass as an id */

  function assistantFromSearch(search) {
    var m = new RegExp('[?&]' + ASSISTANT_PARAM + '=([^&#]*)').exec(search || '');
    if (!m) return null;
    var id;
    try {
      id = decodeURIComponent(m[1].replace(/\+/g, ' ')).trim();
    } catch (e) {
      return null;
    }
    return ASSISTANT_ID.test(id) ? id : null;
  }

  function freshAssistant(stored, nowMs) {
    if (!stored || typeof stored.id !== 'string' || typeof stored.at !== 'number') return null;
    if (!ASSISTANT_ID.test(stored.id) || nowMs - stored.at > ASSISTANT_TTL_MS || stored.at > nowMs) return null;
    return stored.id;
  }

  function currentAssistant() {
    try {
      return freshAssistant(JSON.parse(root.localStorage.getItem(ASSISTANT_KEY) || 'null'), Date.now());
    } catch (e) {
      return null;
    }
  }

  /* Keep the id (if valid) and drop the param from the address either way. */
  function captureAssistant() {
    if (!new RegExp('[?&]' + ASSISTANT_PARAM + '=').test(location.search)) return;
    var id = assistantFromSearch(location.search);
    if (id) {
      try {
        root.localStorage.setItem(ASSISTANT_KEY, JSON.stringify({ id: id, at: Date.now() }));
      } catch (e) {
        /* storage blocked: the order is simply unattributed */
      }
    }
    var url = new URL(location.href);
    url.searchParams.delete(ASSISTANT_PARAM);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  }

  function land() {
    captureAssistant();
    var hit = W.takeParam(location.search, 'add', location.pathname, location.hash);
    if (!hit) return;
    var finish = function () {
      history.replaceState(history.state, '', hit.target);
      if (!hit.lines.length) return; /* nothing usable in the link: the page just loses the param */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      openDrawer();
    };
    addLines(hit.lines).then(capQuantities).then(finish, finish);
  }

  var api = { addLines: addLines, capQuantities: capQuantities, cartRoot: cartRoot, assistantFromSearch: assistantFromSearch, freshAssistant: freshAssistant };
  if (isNode) {
    module.exports = api;
    return;
  }
  root.ErosCartAdd = api;
  root.ErosAssistantAttribution = { current: currentAssistant };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', land);
  else land();
})(typeof window !== 'undefined' ? window : globalThis);
